import React, { useEffect, useState } from 'react';

import { BulkDownloadStatus, Company } from '@hum/types';
import { AppState } from '@hum/icm-app/src/state';
import {
  Icon,
  InfoSection,
  ProgressDownload,
  SectionCard,
  Text,
  TextType,
} from '@hum/ui-library';
import {
  CompanyInfo,
  CompanySignatures,
} from '@hum/icm-app/src/components/CompanyInfo';
import { FinancialInsightsSection } from '@hum/icm-app/src/components/FinancialInsights';
import { CompanyServiceConnections } from './CompanyServiceConnections';
import { DataAssets } from './DataAssets';
import { CompanyFiles } from './CompanyFiles';
import { InternalConfig } from './InternalConfig';
import { DownloadModelFile } from '@hum/icm-app/src/pages/syndication-v2/MatchDataRoom';
import { CompanyExtrasSection } from './CompanyExtrasSection';
import { BulkDownloadModal } from '@hum/icm-app/src/components/BulkDownloadModal';
import { useFlags } from '@hum/icm-app/src/hooks/useFlags';
import { useBulkDownloadFromSelection } from '@hum/icm-app/src/hooks/useBulkDownloadFromSelection';
import { useAnalyticsEvent, getFinancialInsights, useApiQuery } from '@hum/api';

export const CompanyOverview = ({
  companyContact,
  company,
}: {
  companyContact: AppState['currentCompanyUser'];
  company: Company;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const flags = useFlags();
  const bulkDownloadModalEnabled = flags.enabled('enable-bulk-download-modal');
  const trackEvent = useAnalyticsEvent();
  const { fileList, ...bulkDownloadModalProps } = useBulkDownloadFromSelection({
    companyId: company.uuid,
  });
  const { data: insights, isLoading: insightsLoading } = useApiQuery(
    getFinancialInsights,
    {
      params: {
        companyId: company.uuid,
      },
    }
  );

  const isFailure =
    bulkDownloadModalProps.isError ||
    bulkDownloadModalProps.task?.state === BulkDownloadStatus.Failure ||
    bulkDownloadModalProps.task?.state === BulkDownloadStatus.Revoked;
  const isSuccess =
    bulkDownloadModalProps.task?.state === BulkDownloadStatus.Success;

  useEffect(() => {
    trackEvent('insights:company_overview:visit', {}, true);
  }, []);

  return (
    <>
      {bulkDownloadModalEnabled && (
        <BulkDownloadModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          fileList={fileList}
          {...bulkDownloadModalProps}
        />
      )}
      <InfoSection title="Company Info">
        {companyContact.data && (
          <CompanyInfo contact={companyContact.data} company={company} />
        )}
      </InfoSection>

      <CompanyExtrasSection />

      <InfoSection title="Signatures">
        <CompanySignatures company={company} />
      </InfoSection>

      <SectionCard className="my-4 overflow-x-auto rounded custom-scrollbar">
        <Text type={TextType.HEADING_M} className="mb-8">
          Service Connections
        </Text>
        <CompanyServiceConnections companyId={company?.uuid} />
      </SectionCard>

      <InfoSection title="Data Assets">
        <DataAssets company={company} />
      </InfoSection>

      <InfoSection
        title="Financial Files"
        primaryAction={{
          text: 'Download',
          disabled:
            bulkDownloadModalProps.isPolling ||
            (!fileList?.customerFiles.length &&
              !fileList?.supportedFinancialDocuments.length),
          icon: <Icon.Download className="mr-2" width="16" height="16" light />,
          onClick: () => setIsOpen(true),
        }}
      >
        <CompanyFiles company={company} />
      </InfoSection>
      <InfoSection title="Hum standardized data">
        <DownloadModelFile companyId={company.uuid} />
      </InfoSection>
      <InfoSection title="Financial Insights">
        <FinancialInsightsSection
          insights={insights}
          loading={insightsLoading}
        />
      </InfoSection>

      <InfoSection title="Internal Config">
        <InternalConfig companyId={company.uuid} />
      </InfoSection>

      <ProgressDownload
        onCancel={bulkDownloadModalProps.stopDownload}
        isPolling={bulkDownloadModalProps.isPolling}
        isFailure={isFailure}
        isSuccess={isSuccess}
      />
    </>
  );
};
